
import React, { useState, useEffect, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTheme } from "@/contexts/ThemeContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowUp, ArrowDown, Minus, TrendingUp, TrendingDown, AlertTriangle, Users, FileText, Eye, DollarSign, Target, BarChart3, Filter } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TopProgressBar } from "@/components/ui/top-progress-bar";
import { CustomDataTable, ColumnDef } from "@/components/table/CustomDataTable";

interface QuarterlyData {
  id: string;
  language: string;
  year: number;
  quarter: string;
  kolCount: number; 
  kolGrowth: number;
  postCount: number;
  postGrowth: number;
  views: number;
  viewGrowth: number;
  expense: number;
  cpv: number;
  type: string;
  tier: string;
  internal_kol_type: string;
}

interface QuarterlyReportTableProps {
  data?: any[];
}

export const QuarterlyReportTable = ({ data }: QuarterlyReportTableProps) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const { toast } = useToast();
  const [quarterlyData, setQuarterlyData] = useState<QuarterlyData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeYear, setActiveYear] = useState<string>('2025');
  
  // Filter states
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [selectedInternalKolTypes, setSelectedInternalKolTypes] = useState<string[]>([]);
  
  // Define all supported languages
  const allLanguages = ['CN', 'TR', 'EN', 'JP', 'KR', 'ID', 'ES', 'VN', 'RU', 'IN'];
  
  // Define all quarters starting from Q1 2024
  const allYears = [2024, 2025];
  const allQuarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Get unique values for new filters from data
  const allTypes = useMemo(() => 
    [...new Set(quarterlyData.map(item => item.type).filter(Boolean))].sort(), 
    [quarterlyData]
  );
  const allTiers = useMemo(() => 
    [...new Set(quarterlyData.map(item => item.tier).filter(Boolean))].sort(), 
    [quarterlyData]
  );
  const allInternalKolTypes = useMemo(() => 
    [...new Set(quarterlyData.map(item => item.internal_kol_type).filter(Boolean))].sort(), 
    [quarterlyData]
  );

  // Get current date for dynamic year and quarter display
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11
  const currentQuarter = currentMonth < 3 ? 'Q1' : currentMonth < 6 ? 'Q2' : currentMonth < 9 ? 'Q3' : 'Q4';

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    let filtered = quarterlyData;
    
    if (selectedQuarters.length > 0) {
      filtered = filtered.filter(item => selectedQuarters.includes(item.quarter));
    }
    
    if (selectedYears.length > 0) {
      filtered = filtered.filter(item => selectedYears.includes(item.year.toString()));
    }
    
    if (selectedLanguages.length > 0) {
      filtered = filtered.filter(item => selectedLanguages.includes(item.language));
    }

    if (selectedTypes.length > 0) {
      filtered = filtered.filter(item => selectedTypes.includes(item.type));
    }

    if (selectedTiers.length > 0) {
      filtered = filtered.filter(item => selectedTiers.includes(item.tier));
    }

    if (selectedInternalKolTypes.length > 0) {
      filtered = filtered.filter(item => selectedInternalKolTypes.includes(item.internal_kol_type));
    }
    
    return filtered;
  }, [quarterlyData, selectedQuarters, selectedYears, selectedLanguages, selectedTypes, selectedTiers, selectedInternalKolTypes]);

  // Calculate totals based on filtered data for counter cards
  const filteredTotals = useMemo(() => {
    return filteredData.reduce((totals, item) => {
      totals.kolCount += item.kolCount;
      totals.postCount += item.postCount;
      totals.views += item.views;
      totals.expense += item.expense;
      return totals;
    }, {
      kolCount: 0,
      postCount: 0,
      views: 0,
      expense: 0
    });
  }, [filteredData]);

  // Calculate average CPV from filtered data
  const avgCPV = useMemo(() => {
    return filteredTotals.views > 0 ? filteredTotals.expense / filteredTotals.views : 0;
  }, [filteredTotals]);

  // Define columns for the CustomDataTable
  const columns: ColumnDef[] = [
    {
      id: 'language',
      header: 'Language',
      accessorKey: 'language',
      cell: (row) => (
        <Badge variant={isLightMode ? "outline" : "secondary"} className="font-medium">
          {row.language}
        </Badge>
      ),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'year',
      header: 'Year',
      accessorKey: 'year',
      sortable: true,
      minWidth: 80,
    },
    {
      id: 'quarter',
      header: 'Quarter',
      accessorKey: 'quarter',
      sortable: true,
      minWidth: 80,
    },
    {
      id: 'kolCount',
      header: 'No. KOLs',
      accessorKey: 'kolCount',
      cell: (row) => formatNumber(row.kolCount),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'kolGrowth',
      header: "KOL's Growth",
      accessorKey: 'kolGrowth',
      cell: (row) => <GrowthMetric value={row.kolGrowth} currentData={row} type="KOL" />,
      sortable: true,
      minWidth: 120,
    },
    {
      id: 'postCount',
      header: 'No. Post',
      accessorKey: 'postCount',
      cell: (row) => formatNumber(row.postCount),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'postGrowth',
      header: 'Post Growth',
      accessorKey: 'postGrowth',
      cell: (row) => <GrowthMetric value={row.postGrowth} currentData={row} type="Post" />,
      sortable: true,
      minWidth: 120,
    },
    {
      id: 'views',
      header: 'View',
      accessorKey: 'views',
      cell: (row) => formatNumber(row.views),
      sortable: true,
      minWidth: 120,
    },
    {
      id: 'viewGrowth',
      header: 'View Growth',
      accessorKey: 'viewGrowth',
      cell: (row) => <GrowthMetric value={row.viewGrowth} currentData={row} type="View" />,
      sortable: true,
      minWidth: 120,
    },
    {
      id: 'expense',
      header: 'Expense',
      accessorKey: 'expense',
      cell: (row) => formatCurrency(row.expense),
      sortable: true,
      minWidth: 120,
    },
    {
      id: 'cpv',
      header: 'CPV',
      accessorKey: 'cpv',
      cell: (row) => formatCPV(row.cpv),
      sortable: true,
      minWidth: 100,
    },
  ];

  // Fetch data from quarterly_report_view
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      try {
        console.log('Fetching quarterly report data from database view...');
        
        const { data: viewData, error } = await supabase
          .from('quarterly_report_view')
          .select('*')
          .order('year', { ascending: false })
          .order('quarter', { ascending: false })
          .order('language', { ascending: true });

        if (error) {
          console.error("Error fetching quarterly report data:", error);
          throw error;
        }

        console.log(`Fetched ${viewData?.length || 0} records from quarterly_report_view`);

        // Transform database view data to component format
        const transformedData: QuarterlyData[] = (viewData || []).map(item => ({
          id: `${item.language}-${item.year}-${item.quarter}-${item.type}-${item.tier}-${item.internal_kol_type}`,
          language: item.language || 'N/A',
          year: item.year || 0,
          quarter: item.quarter || 'Q1',
          kolCount: item.no_of_kols || 0,
          kolGrowth: item.kol_growth || 0,
          postCount: item.no_of_posts || 0,
          postGrowth: item.post_growth || 0,
          views: item.views || 0,
          viewGrowth: item.view_growth || 0,
          expense: item.expense || 0,
          cpv: item.cpv || 0,
          type: item.type || 'N/A',
          tier: item.tier || 'N/A',
          internal_kol_type: item.internal_kol_type || 'N/A'
        }));

        console.log('Transformed quarterly data:', transformedData.length, 'records');
        setQuarterlyData(transformedData);

      } catch (error) {
        console.error("Error fetching quarterly report data:", error);
        toast({
          title: "Failed to load quarterly report data",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  // Helper function to find previous quarter data for tooltip
  const findPreviousQuarterData = (currentData: QuarterlyData): QuarterlyData | null => {
    const { language, year, quarter } = currentData;
    
    let prevQuarter = '';
    let prevYear = year;
    
    if (quarter === 'Q1') {
      prevQuarter = 'Q4';
      prevYear = year - 1;
    } else if (quarter === 'Q2') {
      prevQuarter = 'Q1';
    } else if (quarter === 'Q3') {
      prevQuarter = 'Q2';
    } else { // Q4
      prevQuarter = 'Q3';
    }
    
    return quarterlyData.find(item => 
      item.language === language && 
      item.year === prevYear && 
      item.quarter === prevQuarter
    ) || null;
  };

  // Enhanced component for growth metrics with visual indicators and proper tooltips
  const GrowthMetric = ({ value, currentData, type }: { value: number; currentData: QuarterlyData; type: 'KOL' | 'Post' | 'View' }) => {
    const isPositive = value > 0;
    const isZero = value === 0;
    const prevData = findPreviousQuarterData(currentData);
    
    const getGrowthIcon = () => {
      if (isZero) return <Minus className="h-3 w-3" />;
      return isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
    };
    
    const getGrowthColor = () => {
      if (isZero) return "text-gray-500";
      return isPositive ? "text-green-600" : "text-red-600";
    };

    const getCurrentValue = () => {
      switch (type) {
        case 'KOL': return currentData.kolCount;
        case 'Post': return currentData.postCount;
        case 'View': return currentData.views;
        default: return 0;
      }
    };

    const getPreviousValue = () => {
      if (!prevData) return 0;
      switch (type) {
        case 'KOL': return prevData.kolCount;
        case 'Post': return prevData.postCount;
        case 'View': return prevData.views;
        default: return 0;
      }
    };

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1 ${getGrowthColor()}`}>
              {getGrowthIcon()}
              <span className="font-medium">{value.toFixed(1)}%</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <p className="font-medium">{type} Growth</p>
              <p>Current Quarter: {formatNumber(getCurrentValue())}</p>
              <p>Previous Quarter: {prevData ? formatNumber(getPreviousValue()) : 'No data'}</p>
              <p>Change: {isPositive ? '+' : ''}{value.toFixed(1)}%</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Filter data by year
  const getDataByYear = (year: number) => {
    return quarterlyData.filter(item => item.year === year);
  };

  const getActualRecordCount = (year: number) => {
    const yearData = getDataByYear(year);
    return yearData.reduce((sum, item) => sum + item.postCount, 0);
  };

  // Enhanced function to calculate comprehensive summary statistics
  const calculateComprehensiveSummary = (yearData: QuarterlyData[]) => {
    if (yearData.length === 0) return null;

    // Group data by language
    const languageGroups: Record<string, QuarterlyData[]> = {};
    yearData.forEach(row => {
      if (!languageGroups[row.language]) {
        languageGroups[row.language] = [];
      }
      languageGroups[row.language].push(row);
    });

    // Calculate totals - focusing on posts instead of KOLs in the main display
    const totals = {
      totalKols: yearData.reduce((sum, row) => sum + row.kolCount, 0),
      totalPosts: yearData.reduce((sum, row) => sum + row.postCount, 0),
      totalViews: yearData.reduce((sum, row) => sum + row.views, 0),
      totalExpense: yearData.reduce((sum, row) => sum + row.expense, 0),
      totalCpv: yearData.reduce((sum, row) => sum + row.cpv, 0),
      avgKolGrowth: yearData.filter(row => row.kolGrowth !== 0).reduce((sum, row, _, arr) => sum + row.kolGrowth / arr.length, 0),
      avgPostGrowth: yearData.filter(row => row.postGrowth !== 0).reduce((sum, row, _, arr) => sum + row.postGrowth / arr.length, 0),
      avgViewGrowth: yearData.filter(row => row.viewGrowth !== 0).reduce((sum, row, _, arr) => sum + row.viewGrowth / arr.length, 0)
    };

    // Language-specific insights - focusing on post counts
    const languageInsights = Object.entries(languageGroups).map(([language, data]) => ({
      language,
      recordCount: data.reduce((sum, row) => sum + row.postCount, 0),
      totalKols: data.reduce((sum, row) => sum + row.kolCount, 0),
      totalPosts: data.reduce((sum, row) => sum + row.postCount, 0),
      totalViews: data.reduce((sum, row) => sum + row.views, 0),
      totalExpense: data.reduce((sum, row) => sum + row.expense, 0),
      avgCpv: data.length > 0 ? data.reduce((sum, row) => sum + row.cpv, 0) / data.length : 0
    })).sort((a, b) => b.totalExpense - a.totalExpense);

    // Performance insights
    const topPerformers = {
      highestViews: languageInsights.reduce((max, curr) => curr.totalViews > max.totalViews ? curr : max, languageInsights[0]),
      lowestCpv: languageInsights.reduce((min, curr) => curr.avgCpv < min.avgCpv && curr.avgCpv > 0 ? curr : min, languageInsights[0]),
      mostPosts: languageInsights.reduce((max, curr) => curr.totalPosts > max.totalPosts ? curr : max, languageInsights[0]),
      mostKols: languageInsights.reduce((max, curr) => curr.totalKols > max.totalKols ? curr : max, languageInsights[0])
    };

    return {
      totals,
      languageInsights,
      topPerformers,
      activeLanguages: languageInsights.length,
      quartersCovered: [...new Set(yearData.map(row => row.quarter))].length
    };
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(Math.round(num));
  };

  const formatCurrency = (num: number) => {
    return `$${new Intl.NumberFormat().format(Math.round(num))}`;
  };

  const formatCPV = (num: number) => {
    return `$${num.toFixed(4)}`;
  };

  // Handle filter changes
  const handleQuarterToggle = (quarter: string) => {
    setSelectedQuarters(prev => 
      prev.includes(quarter) 
        ? prev.filter(q => q !== quarter)
        : [...prev, quarter]
    );
  };

  const handleYearToggle = (year: string) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
  };

  const handleLanguageToggle = (language: string) => {
    setSelectedLanguages(prev => 
      prev.includes(language) 
        ? prev.filter(l => l !== language)
        : [...prev, language]
    );
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleTierToggle = (tier: string) => {
    setSelectedTiers(prev => 
      prev.includes(tier) 
        ? prev.filter(t => t !== tier)
        : [...prev, tier]
    );
  };

  const handleInternalKolTypeToggle = (internalKolType: string) => {
    setSelectedInternalKolTypes(prev => 
      prev.includes(internalKolType) 
        ? prev.filter(i => i !== internalKolType)
        : [...prev, internalKolType]
    );
  };

  const clearAllFilters = () => {
    setSelectedQuarters([]);
    setSelectedYears([]);
    setSelectedLanguages([]);
    setSelectedTypes([]);
    setSelectedTiers([]);
    setSelectedInternalKolTypes([]);
  };

  // Render comprehensive summary section with hover effects - now uses filtered data
  const renderComprehensiveSummary = (year: number) => {
    const yearData = filteredData.filter(item => item.year === year);
    const summary = calculateComprehensiveSummary(yearData);

    if (!summary) return null;

    return (
      <div className={`p-6 rounded-lg border ${isLightMode ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' : 'bg-gradient-to-r from-blue-950/30 to-indigo-950/30 border-blue-800'}`}>
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 className={`h-6 w-6 ${isLightMode ? 'text-blue-600' : 'text-blue-400'}`} />
          <h3 className={`text-xl font-bold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
            {year} Performance Dashboard
            {(selectedQuarters.length > 0 || selectedYears.length > 0 || selectedLanguages.length > 0 || selectedTypes.length > 0 || selectedTiers.length > 0 || selectedInternalKolTypes.length > 0) && (
              <span className="text-sm font-normal ml-2 opacity-75">(Filtered)</span>
            )}
          </h3>
        </div>

        {/* Key Metrics Grid with dynamic data from filtered results */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
          <div className="p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer bg-gradient-to-br from-blue-500 to-blue-600 border-0 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-white opacity-80" />
              <span className="text-sm font-medium text-white opacity-90">
                Total KOLs
              </span>
            </div>
            <div className="text-2xl font-bold text-white">
              {formatNumber(filteredTotals.kolCount)}
            </div>
          </div>

          <div className="p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer bg-gradient-to-br from-green-500 to-green-600 border-0 text-white">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-white opacity-80" />
              <span className="text-sm font-medium text-white opacity-90">
                Total Posts
              </span>
            </div>
            <div className="text-2xl font-bold text-white">
              {formatNumber(filteredTotals.postCount)}
            </div>
          </div>

          <div className="p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer bg-gradient-to-br from-purple-500 to-purple-600 border-0 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-4 w-4 text-white opacity-80" />
              <span className="text-sm font-medium text-white opacity-90">
                Total Views
              </span>
            </div>
            <div className="text-2xl font-bold text-white">
              {formatNumber(filteredTotals.views)}
            </div>
          </div>

          <div className="p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 text-white">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-white opacity-80" />
              <span className="text-sm font-medium text-white opacity-90">
                Total Expense
              </span>
            </div>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(filteredTotals.expense)}
            </div>
          </div>

          <div className="p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer bg-gradient-to-br from-orange-500 to-orange-600 border-0 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-white opacity-80" />
              <span className="text-sm font-medium text-white opacity-90">
                Avg CPV
              </span>
            </div>
            <div className="text-2xl font-bold text-white">
              {formatCPV(avgCPV)}
            </div>
          </div>
        </div>

        {/* Top Performers Section with hover effects */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer ${isLightMode ? 'bg-white border border-green-200 hover:shadow-green-200' : 'bg-background border border-green-800 hover:shadow-green-800/50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className={`text-sm font-medium ${isLightMode ? 'text-green-700' : 'text-green-400'}`}>
                Highest Views
              </span>
            </div>
            <div className={`font-bold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
              {summary.topPerformers.highestViews.language}
            </div>
            <div className={`text-sm ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
              {formatNumber(summary.topPerformers.highestViews.totalViews)} views
            </div>
          </div>

          <div className={`p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer ${isLightMode ? 'bg-white border border-blue-200 hover:shadow-blue-200' : 'bg-background border border-blue-800 hover:shadow-blue-800/50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-blue-600" />
              <span className={`text-sm font-medium ${isLightMode ? 'text-blue-700' : 'text-blue-400'}`}>
                Best CPV
              </span>
            </div>
            <div className={`font-bold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
              {summary.topPerformers.lowestCpv.language}
            </div>
            <div className={`text-sm ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
              {formatCPV(summary.topPerformers.lowestCpv.avgCpv)} avg
            </div>
          </div>

          <div className={`p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer ${isLightMode ? 'bg-white border border-purple-200 hover:shadow-purple-200' : 'bg-background border border-purple-800 hover:shadow-purple-800/50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-purple-600" />
              <span className={`text-sm font-medium ${isLightMode ? 'text-purple-700' : 'text-purple-400'}`}>
                Most Posts
              </span>
            </div>
            <div className={`font-bold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
              {summary.topPerformers.mostPosts.language}
            </div>
            <div className={`text-sm ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
              {formatNumber(summary.topPerformers.mostPosts.totalPosts)} posts
            </div>
          </div>

          <div className={`p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer ${isLightMode ? 'bg-white border border-orange-200 hover:shadow-orange-200' : 'bg-background border border-orange-800 hover:shadow-orange-800/50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-orange-600" />
              <span className={`text-sm font-medium ${isLightMode ? 'text-orange-700' : 'text-orange-400'}`}>
                Most KOLs
              </span>
            </div>
            <div className={`font-bold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
              {summary.topPerformers.mostKols.language}
            </div>
            <div className={`text-sm ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
              {formatNumber(summary.topPerformers.mostKols.totalKols)} KOLs
            </div>
          </div>
        </div>

        {/* Language Breakdown with hover effects */}
        <div className={`p-4 rounded-lg ${isLightMode ? 'bg-white border border-gray-200' : 'bg-background border border-gray-600'}`}>
          <h4 className={`font-semibold mb-3 ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
            Language Performance Breakdown
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {summary.languageInsights.map(({ language, recordCount, totalExpense, avgCpv }) => (
              <div key={language} className={`p-3 rounded border transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer ${isLightMode ? 'bg-gray-50 border-gray-200 hover:shadow-gray-300' : 'bg-muted/30 border-gray-700 hover:shadow-gray-700/50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant={isLightMode ? "outline" : "secondary"} className="font-medium">
                    {language}
                  </Badge>
                  <span className={`text-xs ${isLightMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {recordCount} posts
                  </span>
                </div>
                <div className={`text-sm font-medium ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
                  {formatCurrency(totalExpense)}
                </div>
                <div className={`text-xs ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
                  {formatCPV(avgCpv)} avg CPV
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render table for specific year
  const renderYearTable = (year: number) => {
    const yearData = filteredData.filter(item => item.year === year);

    return (
      <div className="space-y-6">
        {/* Enhanced Summary Section */}
        {renderComprehensiveSummary(year)}

        {/* Table Section with Filters */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-semibold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>
              Detailed Quarterly Data
            </h3>
          </div>
          
          {/* Filters */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Quarter Filter */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                  Quarter
                </label>
                <div className="flex flex-wrap gap-2">
                  {allQuarters.map(quarter => (
                    <Button
                      key={quarter}
                      variant={selectedQuarters.includes(quarter) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleQuarterToggle(quarter)}
                    >
                      {quarter}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Year Filter */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                  Year
                </label>
                <div className="flex flex-wrap gap-2">
                  {allYears.map(filterYear => (
                    <Button
                      key={filterYear}
                      variant={selectedYears.includes(filterYear.toString()) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleYearToggle(filterYear.toString())}
                    >
                      {filterYear}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Language Filter */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                  Language
                </label>
                <div className="flex flex-wrap gap-2">
                  {allLanguages.map(language => (
                    <Button
                      key={language}
                      variant={selectedLanguages.includes(language) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleLanguageToggle(language)}
                    >
                      {language}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Type Filter */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                  Type
                </label>
                <div className="flex flex-wrap gap-2">
                  {allTypes.map(type => (
                    <Button
                      key={type}
                      variant={selectedTypes.includes(type) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTypeToggle(type)}
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Tier Filter */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                  Tier
                </label>
                <div className="flex flex-wrap gap-2">
                  {allTiers.map(tier => (
                    <Button
                      key={tier}
                      variant={selectedTiers.includes(tier) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTierToggle(tier)}
                    >
                      {tier}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Internal KOL Type Filter */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                  Internal KOL Type
                </label>
                <div className="flex flex-wrap gap-2">
                  {allInternalKolTypes.map(internalKolType => (
                    <Button
                      key={internalKolType}
                      variant={selectedInternalKolTypes.includes(internalKolType) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleInternalKolTypeToggle(internalKolType)}
                    >
                      {internalKolType}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Active Filters */}
            {(selectedQuarters.length > 0 || selectedYears.length > 0 || selectedLanguages.length > 0) && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
                    Active Filters:
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearAllFilters}
                  >
                    Clear All
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedQuarters.map(quarter => (
                    <Badge key={quarter} variant="secondary" className="gap-1">
                      Q: {quarter}
                      <button onClick={() => handleQuarterToggle(quarter)} className="ml-1 text-xs">×</button>
                    </Badge>
                  ))}
                  {selectedYears.map(filterYear => (
                    <Badge key={filterYear} variant="secondary" className="gap-1">
                      Year: {filterYear}
                      <button onClick={() => handleYearToggle(filterYear)} className="ml-1 text-xs">×</button>
                    </Badge>
                  ))}
                  {selectedLanguages.map(language => (
                    <Badge key={language} variant="secondary" className="gap-1">
                      Lang: {language}
                      <button onClick={() => handleLanguageToggle(language)} className="ml-1 text-xs">×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Custom Data Table - no totals footer */}
          <div className="relative">
            <CustomDataTable
              data={yearData}
              columns={columns}
              enableSorting={true}
              showPagination={false}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <p className="text-muted-foreground">Loading quarterly data...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Enhanced Year Tabs */}
          <Tabs value={activeYear} onValueChange={setActiveYear} className="w-full">
            <TabsList className={`grid w-full grid-cols-2 ${isLightMode ? 'bg-gray-100' : ''}`}>
              <TabsTrigger 
                value="2024" 
                className={`transition-all duration-200 ${isLightMode ? 'data-[state=active]:bg-white data-[state=active]:text-gray-900' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">2024</span>
                  <Badge variant="secondary" className="text-xs">
                    {getActualRecordCount(2024)} posts
                  </Badge>
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="2025"
                className={`transition-all duration-200 ${isLightMode ? 'data-[state=active]:bg-white data-[state=active]:text-gray-900' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">2025</span>
                  <Badge variant="secondary" className="text-xs">
                    {getActualRecordCount(2025)} posts
                  </Badge>
                </div>
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="2024" className="mt-6 animate-fade-in">
              {renderYearTable(2024)}
            </TabsContent>
            
            <TabsContent value="2025" className="mt-6 animate-fade-in">
              {renderYearTable(2025)}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </>
  );
};
